import { Reveal, Kicker } from "@/components/Reveal";
import { LeadForm } from "@/components/LeadForm";
import { Marquee } from "@/components/Marquee";
import { Globe2, PackageCheck, ClipboardCheck } from "lucide-react";

const MODELS = [
  {
    icon: Globe2,
    title: "Territory licensing",
    body: "Exclusive or first-look licences for defined territories, granted to qualified manufacturers, licensees and distributors.",
  },
  {
    icon: PackageCheck,
    title: "Sole-source ingredient supply",
    body: "Direct supply of the active from our Wisconsin operation · one specification, batch-level documentation, security of supply.",
  },
  {
    icon: ClipboardCheck,
    title: "Technical & regulatory support",
    body: "Analytical documentation and formulation guidance supporting partner registrations. Partners remain responsible for regulatory approvals in their own markets.",
  },
];

export default function Partnering() {
  return (
    <div data-testid="partnering-page">
      <section className="hero-grid pt-32 lg:pt-40 pb-16 lg:pb-20 border-b border-line">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal y={16}>
            <Kicker index="§4">Partnering & Supply</Kicker>
            <h1 className="mt-6 font-headline font-semibold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.06] max-w-4xl">
              Three ways to work with us.
            </h1>
            <p className="mt-7 text-base sm:text-lg text-smoke leading-relaxed max-w-3xl">
              We partner with international pet-product manufacturers, licensees, distributors and
              veterinary formulators. Enquiries are reviewed directly by our Madison office.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24" data-testid="partnering-models">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-px bg-line border border-line">
          {MODELS.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.1} className="bg-white p-8 lg:p-10 hover:bg-pinelight/30 transition-colors duration-300">
              <m.icon className="text-pine" size={26} strokeWidth={1.5} />
              <h2 className="mt-6 font-headline font-semibold text-xl text-ink tracking-tight">{m.title}</h2>
              <p className="mt-3 text-sm text-smoke leading-relaxed">{m.body}</p>
            </Reveal>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm text-faint max-w-3xl leading-relaxed">
              Regulatory responsibility: partners are responsible for obtaining and maintaining
              regulatory approvals for finished products in their own markets. We supply supporting
              technical documentation under NDA.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="py-20 lg:py-24 bg-white border-t border-line" data-testid="partnering-form-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>Partnership enquiry</Kicker>
              <h2 className="mt-5 font-headline font-semibold tracking-tight text-ink text-2xl sm:text-3xl lg:text-4xl leading-tight">
                Start a conversation.
              </h2>
              <p className="mt-5 text-sm sm:text-base text-smoke leading-relaxed max-w-md">
                Tell us who you are, where you operate and which territory you are evaluating.
                Qualified enquiries receive the confidential dossier pathway, beginning with an
                executed NDA.
              </p>
              <div className="mt-8 border border-line divide-y divide-line font-mono text-[11px] uppercase tracking-[0.18em] text-faint">
                <p className="px-5 py-3.5">01 · Enquiry reviewed in Madison, WI</p>
                <p className="px-5 py-3.5">02 · Mutual NDA executed</p>
                <p className="px-5 py-3.5">03 · Dossier & binding data shared</p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.12}>
              <div className="border border-line bg-paper p-6 sm:p-10">
                <LeadForm variant="partnership" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
